import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import type {
  Brand,
  Company,
  Domain,
  LegalTexts,
  OrganizationChecklistData,
  SetupOrganization,
  StepAction,
} from './types';
import type { OrganizationSetupStepKey } from '@brimo/shared';

/**
 * Query keys of the organization module: everything under `['organization']` and `['setup']`, the roots the realtime
 * event `organization.updated` refreshes (lib/events.ts).
 */
export const organizationKeys = {
  company: ['organization', 'company'] as const,
  brand: ['organization', 'brand'] as const,
  legalTexts: ['organization', 'legal-texts'] as const,
  legalText: (type: string) => ['organization', 'legal-text', type] as const,
  domain: ['organization', 'domain'] as const,
  itMail: ['organization', 'it-mail'] as const,
  sendingDomain: ['organization', 'sending-domain'] as const,
  wizard: ['setup', 'organization'] as const,
  checklist: ['setup', 'checklist', 'organization'] as const,
};

export function useWizard() {
  return useQuery({
    queryKey: organizationKeys.wizard,
    queryFn: () => api<SetupOrganization>('/setup/organization'),
  });
}

export function postStep(key: OrganizationSetupStepKey, action: StepAction): Promise<SetupOrganization> {
  return api<SetupOrganization>(`/setup/organization/steps/${key}`, { method: 'POST', body: { action } });
}

export function useCompany() {
  return useQuery({
    queryKey: organizationKeys.company,
    queryFn: () => api<Company>('/settings/organization/company'),
  });
}

export function useBrand() {
  return useQuery({
    queryKey: organizationKeys.brand,
    queryFn: () => api<Brand>('/settings/organization/brand'),
    // A fresh logo is quarantined until the scan job marks it clean; poll until then.
    refetchInterval: (query) =>
      query.state.data && Object.values(query.state.data.logos).some((logo) => logo?.scanStatus === 'pending')
        ? 2500
        : false,
  });
}

export function useLegalTexts() {
  return useQuery({
    queryKey: organizationKeys.legalTexts,
    queryFn: () => api<LegalTexts>('/settings/organization/legal-texts'),
  });
}

export function useDomain(options: { refetchInterval?: number | false } = {}) {
  return useQuery({
    queryKey: organizationKeys.domain,
    queryFn: () => api<Domain>('/settings/organization/domain'),
    refetchInterval: options.refetchInterval ?? false,
  });
}

export function useOrganizationChecklist() {
  return useQuery({
    queryKey: organizationKeys.checklist,
    queryFn: () => api<OrganizationChecklistData>('/setup/checklist/organization'),
  });
}

/**
 * Multipart upload with progress (`XMLHttpRequest`, no dependency): resolves with the parsed JSON, rejects with the
 * API's error envelope wrapped like `api()` does.
 */
export function uploadWithProgress<T>(
  path: string,
  form: FormData,
  onProgress: (fraction: number) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', `/api${path}`);
    request.setRequestHeader('Accept', 'application/json');
    request.withCredentials = true;
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    request.onerror = () => reject(new UploadError(0, null));
    request.onload = () => {
      let json: unknown = null;
      try {
        json = request.responseText ? JSON.parse(request.responseText) : null;
      } catch {
        json = null;
      }
      if (request.status >= 200 && request.status < 300) resolve(json as T);
      else reject(new UploadError(request.status, json));
    };
    request.send(form);
  });
}

/** Failure of `uploadWithProgress` with the server's German message (or a generic one). */
export class UploadError extends Error {
  readonly status: number;
  constructor(status: number, body: unknown) {
    const envelope = typeof body === 'object' && body !== null && 'error' in body ? body.error : null;
    const message =
      typeof envelope === 'object' &&
      envelope !== null &&
      'message' in envelope &&
      typeof envelope.message === 'string'
        ? envelope.message
        : status === 413
          ? 'Die Datei ist zu groß.'
          : 'Die Datei konnte nicht hochgeladen werden. Bitte versuchen Sie es erneut.';
    super(message);
    this.name = 'UploadError';
    this.status = status;
  }
}
